<!--
Copyright 2024 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->

<div class="cssLayoutRowStartCenter">
  <div class="sp-icon">
    <mat-icon class="mdi-24px" fontIcon="mdi-application" [ngStyle]="{'color': 'var(--mat-sys-primary)'}"></mat-icon>
  </div>
</div>

<mat-menu #addMenu="matMenu" [overlapTrigger]="false">
  <button mat-menu-item (click)="addOrEditPlayerApplication(null, 'Add a Player Application')"
    title="Add a new player application">New Player
    Application</button>
  @for (template of templateList; track $index) {
  <button mat-menu-item (click)="addOrEditPlayerApplication(template, 'Add a Player Application')"
    title="{{ template.url }}">{{ template.name
    }}</button>
  }
</mat-menu>

@if (msel) {
@if (msel.status !== 'Deployed' && casterVmAppWarningMessage()) {
<div class="vm-app-warning">{{ casterVmAppWarningMessage() }}</div>
}
<div class="frame">
  <mat-expansion-panel>
    <mat-expansion-panel-header class="team-app-order-header">
      <h4>Player Applications</h4>
    </mat-expansion-panel-header>
    <table mat-table #appTable [dataSource]="appDataSource" matSort (matSortChange)="sortChanged($event)"
      multiTemplateDataRows>
      <!-- action column -->
      <ng-container matColumnDef="action">
        <th mat-header-cell *matHeaderCellDef class="column-action">
          @if (canEditMsel() || msel.hasRole(loggedInUserId, null).owner) {
          <button mat-icon-button [matMenuTriggerFor]="addMenu" style="outline: none;" title="Add Player Application"
            (click)="$event.stopPropagation();">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-plus-circle"></mat-icon>
          </button>
          }
        </th>
        <td mat-cell *matCellDef="let element" class="column-action">
          @if (canEditMsel() || msel.hasRole(loggedInUserId, null).owner) {
          <button mat-icon-button
            (click)="addOrEditPlayerApplication(element, 'Edit Player Application'); $event.stopPropagation()"
            title="Edit {{ element.name }}">
            <mat-icon class="mdi-18px" fontIcon="mdi-square-edit-outline"></mat-icon>
          </button>
          }
          @if (canEditMsel() || msel.hasRole(loggedInUserId, null).owner) {
          <button mat-icon-button (click)="deletePlayerApplication(element); $event.stopPropagation()"
            title="Delete {{ element.name }}">
            <mat-icon class="mdi-18px" fontIcon="mdi-trash-can-outline"></mat-icon>
          </button>
          }
        </td>
      </ng-container>
      <!-- name column -->
      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="column-name">Name</th>
        <td mat-cell *matCellDef="let element" class="column-name">{{ element.name }}</td>
      </ng-container>
      <!-- url column -->
      <ng-container matColumnDef="url">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="column-url">URL</th>
        <td mat-cell *matCellDef="let element" class="column-url">{{ element.url }}</td>
      </ng-container>
      <!-- icon column -->
      <ng-container matColumnDef="icon">
        <th mat-header-cell *matHeaderCellDef class="column-icon">Icon</th>
        <td mat-cell *matCellDef="let element" class="column-icon" [title]="element.icon || ''">
          @if (element.icon?.startsWith('mdi-')) {
          <mat-icon class="mdi-24px" [fontIcon]="element.icon"></mat-icon>
          } @else if (getIconUrl(element.icon) && !brokenIcons.has(element.icon)) {
          <img class="app-icon" [src]="getIconUrl(element.icon)" [alt]="element.name"
            (error)="brokenIcons.add(element.icon)" />
          }
        </td>
      </ng-container>
      <!-- embeddable column -->
      <ng-container matColumnDef="embeddable">
        <th mat-header-cell *matHeaderCellDef class="column-flag"
          matTooltip="Player shows the application inside the view instead of opening it in a new tab">Embeddable</th>
        <td mat-cell *matCellDef="let element" class="column-flag">
          <mat-checkbox [checked]="element.embeddable" disabled (click)="$event.stopPropagation();"></mat-checkbox>
        </td>
      </ng-container>
      <!-- loadInBackground column -->
      <ng-container matColumnDef="loadInBackground">
        <th mat-header-cell *matHeaderCellDef class="column-flag"
          matTooltip="Player loads the application when the view opens, before a participant selects it">Load in Background</th>
        <td mat-cell *matCellDef="let element" class="column-flag">
          <mat-checkbox [checked]="element.loadInBackground" disabled (click)="$event.stopPropagation();"></mat-checkbox>
        </td>
      </ng-container>
      <!-- Expanded Content Column -->
      <ng-container matColumnDef="expandedDetail">
        <td mat-cell *matCellDef="let element" [attr.colspan]="displayedColumns.length">
          <div [@detailExpand]="element.id == expandedId ? 'expanded' : 'collapsed'" style="overflow: hidden">
            @if (expandedId === element.id) {
            <div class="expanded-detail-div" (click)="$event.stopPropagation();">
              <div class="single-detail">
                <span class="title">Select the MSEL teams that will see this application</span>
              </div>
              <div class="single-detail">
                <app-player-application-teams style="width: 100%;" [loggedInUserId]="loggedInUserId"
                  [playerApplicationId]="element.id" [mselTeamList]="mselTeamList">
                </app-player-application-teams>
              </div>
            </div>
            }
          </div>
        </td>
      </ng-container>
      <!-- row definitions -->
      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;" [class]="getRowClass(row.id)"
        (click)="rowClicked(row)"></tr>
      <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row"></tr>
    </table>
  </mat-expansion-panel>
  <mat-expansion-panel>
    <mat-expansion-panel-header class="team-app-order-header">
      <h4>Team Application Order</h4>
    </mat-expansion-panel-header>
    <div class="playerApplication-detail" (click)="$event.stopPropagation();">
      <div class="single-detail">
        <app-player-team-app-order style="width: 100%;" [mselTeamList]="mselTeamList"
          [canEdit]="canEditMsel() || msel.hasRole(loggedInUserId, null).owner">
        </app-player-team-app-order>
      </div>
    </div>
  </mat-expansion-panel>
</div>
}
